import {
  ChatConversation,
  ChatConversationRequestOptions,
  ChatSnapshot,
  CreateConversationPayload,
} from "../types";
import { CHAT_API_BASE_URL } from "./config";
import { normalizeConversation } from "../utils/normalizers";
import { mergeRoster } from "../utils/snapshotMutations";
import { createDefaultSnapshot } from "../utils/snapshot";

const DEFAULT_BASE_URL = "/api/chat";

const buildUrl = (path: string) => {
  const base = CHAT_API_BASE_URL || DEFAULT_BASE_URL;
  if (!path) return base;
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
};

function buildHeaders(token: string): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchSnapshot(token: string): Promise<ChatSnapshot> {
  const response = await fetch(buildUrl("/snapshot"), {
    method: "GET",
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`Failed to load chat snapshot (status ${response.status})`);
  }

  const data = await response.json();
  const roster = data?.roster ?? {};
  const conversations = data?.conversations ?? {};
  let snapshot = createDefaultSnapshot();

  const conversationsToMerge: ChatConversation[] = [];
  for (const value of Object.values(roster)) {
    try {
      conversationsToMerge.push(normalizeConversation(value));
    } catch (error) {
      console.warn("Skipping invalid conversation in roster", error);
    }
  }

  snapshot = mergeRoster(snapshot, conversationsToMerge);

  for (const category of ["inbox", "unread", "communities"] as const) {
    const items = conversations?.[category];
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      try {
        snapshot = mergeRoster(snapshot, [normalizeConversation(item)]);
      } catch (error) {
        console.warn(`Skipping invalid conversation in ${category}`, error);
      }
    }
  }

  return snapshot;
}

function buildQuery(options: ChatConversationRequestOptions | undefined) {
  if (!options) return "";
  const params = new URLSearchParams();
  if (typeof options.page === "number") params.set("page", String(options.page));
  if (typeof options.size === "number") params.set("size", String(options.size));
  if (options.before) params.set("before", new Date(options.before).toISOString());
  if (options.after) params.set("after", new Date(options.after).toISOString());
  const query = params.toString();
  return query ? `?${query}` : "";
}

export async function fetchConversation(
  token: string,
  conversationId: string,
  options?: ChatConversationRequestOptions
): Promise<ChatConversation | null> {
  const url = `${buildUrl(`/conversations/${encodeURIComponent(conversationId)}`)}${buildQuery(options)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(token),
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to load conversation ${conversationId} (status ${response.status})`);
  }

  const data = await response.json();
  return normalizeConversation(data);
}

export async function createConversation(
  token: string,
  payload: CreateConversationPayload
): Promise<ChatConversation> {
  const response = await fetch(buildUrl("/conversations"), {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to create conversation (status ${response.status})`);
  }

  const data = await response.json();
  return normalizeConversation(data);
}

export async function markConversationAsRead(token: string, conversationId: string, userId: string): Promise<ChatConversation> {
  const response = await fetch(buildUrl(`/conversations/${encodeURIComponent(conversationId)}/read`), {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to mark conversation as read (status ${response.status})`);
  }

  const data = await response.json();
  return normalizeConversation(data);
}

